
import { ref, defineComponent, onMounted, computed } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { Actions } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as Yup from "yup";
import Avatar from "vue-avatar-component";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { rutEsValido, formatRut, yupIsRut } from "@/core/validators/YupRut";

interface ProveedorDetails {
  nombreProveedor: string;
  rutProveedor: string;
}

interface ProfileDetails {
  tokenUsuario: string;
  rutProveedor: string;
  rut: string;
  codigoRol: string;
  proveedorId: number;
  nombres: string;
  apellidos: string;
  telefono: number;
  email: string;
  activo: boolean;
}

interface ProfileSecurity {
  tokenUsuario: string;
  nuevaContrasena: string;
  confirmacionNuevaContrasena: string;
}

export default defineComponent({
  name: "profile-settings",
  components: {
    ErrorMessage,
    Field,
    Form,
    Avatar,
  },
  setup() {
    const submitButton1 = ref<HTMLElement | null>(null);
    const submitButton2 = ref<HTMLElement | null>(null);
    const submitButton3 = ref<HTMLElement | null>(null);
    const submitButton4 = ref<HTMLElement | null>(null);
    const submitButton5 = ref<HTMLElement | null>(null);
    const updateEmailButton = ref<HTMLElement | null>(null);
    const updatePasswordButton = ref<HTMLElement | null>(null);
    const confirm = useConfirm();
    const toast = useToast();
    const emailFormDisplay = ref(false);
    const passwordFormDisplay = ref(false);
    Yup.addMethod(Yup.string, "yupIsRut", function (mensaje) {
      const { message } = mensaje;
      return this.test("yupIsRut", message, function (value) {
        const { path, createError } = this;
        const { some, more, args } = mensaje;
        // [value] - value of the property being tested
        // [path]  - property name,
        // ...
        return rutEsValido(value);
      });
    });

    const profileDetailsValidator = Yup.object().shape({
      nombres: Yup.string().required("Es obligatorio").label("Nombre"),
      apellidos: Yup.string().required("Es obligatorio").label("Apellido"),
      telefono: Yup.string().required("Es obligatorio").label("Teléfono"),
      email: Yup.string().required("Es obligatorio").label("Email"),
      rut: Yup.string()
        .test("yupIsRut", "Rut ingresado no es valido", function (value) {
          return rutEsValido(value);
        })
        .required("Es requerido")
        .label("Rut"),
    });

    const changeEmail = Yup.object().shape({
      emailaddress: Yup.string().required().email().label("Email"),
      confirmemailpassword: Yup.string().required().label("Password"),
    });

   const changePassword = Yup.object().shape({
    contrasena: Yup.string()
        // .required("Es obligatorio")
        .label("Contraseña"),
    nuevaContrasena: Yup.string()
        .required("Es obligatorio")
        .min(8, "Por favor ingresa al menos 8 caracteres")
        .max(100, "La contraseña no puede tener más de 100 caracteres")
        .matches(
            /[!#%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/,
            "Debe contener al menos un carácter especial"
        )
        .matches(/[a-z]/, "Debe contener al menos una letra minúscula")
        .matches(/[A-Z]/, "Debe contener al menos una letra mayúscula")
        .matches(/\d/, "Debe contener al menos un número")
        .label("Nueva Contraseña"),
    confirmacionNuevaContrasena: Yup.string()
        .required("Es obligatorio")
        .oneOf(
            [Yup.ref("nuevaContrasena"), null],
            "La confirmación debe coincidir con la nueva"
        )
        .label("Confirmación de contraseña"),
});


    const aprobar = (event) => {
      let usuario = profileDetails.value;
      usuario.activo = true;
      confirm.require({
        target: event.currentTarget,
        message: "¿Está seguro de Reactivar la cuenta del usuario?",
        icon: "pi pi-info-circle",
        acceptLabel: "Si",
        rejectLabel: "No",
        acceptClass: "p-button-danger",
        accept: () => {
          store
            .dispatch(Actions.UPD_USUARIO_UP, usuario)
            .then(() => {
              profileDetails.value.activo = true;
              Swal.fire({
                text: "La cuenta ha sido reactivada. Hemos enviado un correo electrónico informando al usuario",
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: "Ok!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-primary",
                },
              });
            })
            .catch(() => {
              const [error] = Object.keys(store.getters.getUsuarioErrors);
              Swal.fire({
                text: store.getters.getUsuarioErrors[error],
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Intenar nuevamente!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-danger",
                },
              });
            });
        },
        reject: () => {
          toast.add({
            severity: "error",
            summary: "Rejected",
            detail: "You have rejected",
            life: 3000,
          });
        },
      });
    };
    const rechazar = (event) => {
      let usuario = profileDetails.value;
      usuario.activo = true;
      confirm.require({
        target: event.currentTarget,
        message: "Está seguro de desactivar la cuenta del usuario?",
        icon: "pi pi-info-circle",
        acceptLabel: "Si",
        rejectLabel: "No",
        acceptClass: "p-button-danger",
        accept: () => {
          store
            .dispatch(Actions.UPD_USUARIO_DOWN, usuario)
            .then(() => {
              profileDetails.value.activo = false;
              Swal.fire({
                text: "La cuenta ha sido desactivada. Hemos enviado un correo electrónico informando al usuario",
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: "Ok!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-primary",
                },
              });
            })
            .catch(() => {
              const [error] = Object.keys(store.getters.getUsuarioErrors);
              Swal.fire({
                text: store.getters.getUsuarioErrors[error],
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Intenar nuevamente!",
                customClass: {
                  confirmButton: "btn fw-bold btn-light-danger",
                },
              });
            });
        },
        reject: () => {
          toast.add({
            severity: "error",
            summary: "Rejected",
            detail: "You have rejected",
            life: 3000,
          });
        },
      });
    };
    const saveChanges1 = () => {
      if (submitButton1.value) {
        // Activate indicator
        submitButton1.value.setAttribute("data-kt-indicator", "on");
        store
          .dispatch(Actions.UPD_USUARIO, profileDetails.value)
          .then(() => {
            loading = ref(false);
            submitButton1.value?.removeAttribute("data-kt-indicator");
            Swal.fire({
              text: "La información del usuario ha sido actualizada correctamente",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok",
              customClass: {
                confirmButton: "btn fw-bold btn-light-primary",
              },
            });
          })
          .catch(() => {
            const [error] = Object.keys(store.getters.getUsuarioErrors);
            Swal.fire({
              text: store.getters.getUsuarioErrors[error],
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Intenar nuevamente!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          });
      }
    };

    const updatePassword = () => {
      if (updatePasswordButton.value) {
        // Activate indicator
        updatePasswordButton.value.setAttribute("data-kt-indicator", "on");
        setTimeout(() => {
          updatePasswordButton.value?.removeAttribute("data-kt-indicator");
          store
            .dispatch(
              Actions.UPD_RESETEO_CONTRASENA_USUARIO,
              profileSecurity.value
            )
            .then(() => {
              loading = ref(false);
              submitButton1.value?.removeAttribute("data-kt-indicator");
              Swal.fire({
                text: "La contraseña se ha modificado correctamente. Hemos enviado un correo electrónico al usuario con la información de su acceso.",
                icon: "success",
                confirmButtonText: "Ok",
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              }).then(() => {
                passwordFormDisplay.value = false;
              });
            })
            .catch(() => {
              const [error] = Object.keys(store.getters.getUsuarioErrors);
              Swal.fire({
                text: store.getters.getUsuarioErrors[error],
                icon: "warning",
                confirmButtonText: "Ok",
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              }).then(() => {
                passwordFormDisplay.value = false;
              });
            });
        }, 2000);
      }
    };
    const store = useStore();
    const route = useRoute();
    const id = route.params.id;
    onMounted(() => {
      setCurrentPageBreadcrumbs("Modificar Datos", ["Páginas", "Dashboard"]);
      store
        .dispatch(Actions.GET_USUARIO, id)
        .then(() => {
          loading = ref(false);
          profileDetails.value = store.getters.currentUsuario;
          profileSecurity.value = {
            tokenUsuario: store.getters.currentUsuario.tokenUsuario,
            nuevaContrasena: "",
            confirmacionNuevaContrasena: "",
          };
        })
        .catch(() => {
          const [error] = Object.keys(store.getters.getUsuarioErrors);
        });
      store
        .dispatch(
          Actions.GET_PROVEEDOR
        )
        .then(() => {
          loading = ref(false);
          currentProveedor.value = store.getters.currentProveedor;
        })
        .catch(() => {
          const [error] = Object.keys(store.getters.getUsuarioErrors);
        });
    });

    const currentProveedor = ref<ProveedorDetails>({
      nombreProveedor: store.getters.currentProveedor.nombreProveedor,
      rutProveedor: store.getters.currentProveedor.rutProveedor,
    });
    const profileDetails = ref<ProfileDetails>({
      tokenUsuario: store.getters.currentUsuario.tokenUsuario,
      nombres: store.getters.currentUsuario.nombres,
      apellidos: store.getters.currentUsuario.apellidos,
      email: store.getters.currentUsuario.email,
      telefono: store.getters.currentUsuario.telefono,
      rut: store.getters.currentUsuario.rut,
      rutProveedor: store.getters.currentUsuario.rutProveedor,
      codigoRol: store.getters.currentUsuario.codigoRol,
      proveedorId: store.getters.currentUsuario.proveedorId,
      activo: store.getters.currentUsuario.activo,
    });
    const profileSecurity = ref<ProfileSecurity>({
      tokenUsuario: store.getters.currentUsuario.tokenUsuario,
      nuevaContrasena: "",
      confirmacionNuevaContrasena: "",
    });
    let loading = ref(true);
    return {
      submitButton1,
      submitButton2,
      submitButton3,
      submitButton4,
      submitButton5,
      saveChanges1,
      profileDetails,
      profileSecurity,
      emailFormDisplay,
      passwordFormDisplay,
      profileDetailsValidator,
      changeEmail,
      changePassword,
      updateEmailButton,
      updatePasswordButton,
      updatePassword,
      currentProveedor,
      aprobar,
      rechazar,
    };
  },
});
